import React, { useContext, useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { PlaylistsContext } from 'contexts/playlists';
import {
  FilterTitle,
  Filters,
  InfoIcon,
  SingleFilterContainer,
  TypeFiltersContainer,
} from './styles';
import Checkbox from 'components/checkbox';
import { PLAYLIST_TYPE } from 'constants/playlists';
import Tooltip from 'components/tooltip';
import { SessionContext } from 'contexts/session';
import Radio from 'components/radio/Radio';

const PlaylistFilters = (): JSX.Element => {
  const { user } = useContext(SessionContext);
  const { actions, filters, fetching } = useContext(PlaylistsContext);
  const { isSonarEnabled } = useContext(SessionContext);
  const initialState = {
    [PLAYLIST_TYPE.STANDARD]: true,
    [PLAYLIST_TYPE.SONAR_RECOMMENDED]: true,
    assigned: true,
    unassigned: true,
    allUsers: true,
    currentUser: false,
  };
  const [value, setValue] = useState(initialState);

  const handleTypeFilterChanger = (newValue) => {
    if (newValue[PLAYLIST_TYPE.STANDARD] === newValue[PLAYLIST_TYPE.SONAR_RECOMMENDED]) {
      actions && actions.onClearTypeFilters();
      return;
    }

    const typeToSearch = newValue[PLAYLIST_TYPE.STANDARD]
      ? PLAYLIST_TYPE.STANDARD
      : PLAYLIST_TYPE.SONAR_RECOMMENDED;

    actions && actions.onChangeFilters({ name: 'type', value: typeToSearch }, false, true);
  };

  const handleAssignedFilterChanger = (newValue) => {
    if (newValue.assigned === newValue.unassigned) {
      actions && actions.onClearAssignedFilters();
      return;
    }

    const stateToSearch = newValue.assigned;

    actions && actions.onChangeFilters({ name: 'assigned', value: stateToSearch }, false, true);
  };

  const handleCreationFilterChanger = (newValue) => {
    if (newValue.allUsers) {
      actions && actions.onClearCreationFilters();
      return;
    }

    const stateToSearch = user?.uaa_id;

    actions && actions.onChangeFilters({ name: 'createdBy', value: stateToSearch }, false, true);
  };

  const handleFilterChange = (e) => {
    const currentValue = {
      name: e.target.name,
      value: e.target.checked,
    };
    const newValue = {
      ...value,
      [currentValue.name]: currentValue.value,
    };
    setValue(newValue);
    switch (e.target.name) {
      case PLAYLIST_TYPE.STANDARD:
      case PLAYLIST_TYPE.SONAR_RECOMMENDED:
        handleTypeFilterChanger(newValue);
        break;
      case 'allUsers':
        newValue.currentUser = !newValue.allUsers;
        handleCreationFilterChanger(newValue);
        break;
      case 'currentUser':
        newValue.allUsers = !newValue.currentUser;
        handleCreationFilterChanger(newValue);
        break;
      default:
        handleAssignedFilterChanger(newValue);
    }
  };

  const renderCheckbox = (singleFilters) => {
    return (
      <>
        {singleFilters.map((singleFilter) => (
          <SingleFilterContainer key={singleFilter.id}>
            <Checkbox
              label={singleFilter.label}
              value={singleFilter.id}
              id={singleFilter.id}
              name={singleFilter.id}
              onChange={handleFilterChange}
              defaultChecked={false}
              checked={value[singleFilter.id]}
              controlled
              darkBackground
              disabled={fetching}
            />
            {singleFilter.tooltip && (
              <Tooltip
                placement="top"
                title={singleFilter.tooltip}
                overlayInnerStyle={{ textAlign: 'center' }}
              >
                <InfoIcon size={isMobile ? 'smaller' : 'small'} icon="Info" />
              </Tooltip>
            )}
          </SingleFilterContainer>
        ))}
      </>
    );
  };

  const renderRadio = (singleFilters) => {
    return (
      <>
        {singleFilters.map((singleFilter) => (
          <SingleFilterContainer key={singleFilter.id}>
            <Radio
              label={singleFilter.label}
              value={singleFilter.id}
              id={singleFilter.id}
              name={singleFilter.id}
              onChange={handleFilterChange}
              checked={value[singleFilter.id]}
              controlled
              darkBackground
              disabled={fetching}
            />
            {singleFilter.tooltip && (
              <Tooltip
                placement="top"
                title={singleFilter.tooltip}
                overlayInnerStyle={{ textAlign: 'center' }}
              >
                <InfoIcon size={isMobile ? 'smaller' : 'small'} icon="Info" />
              </Tooltip>
            )}
          </SingleFilterContainer>
        ))}
      </>
    );
  };

  useEffect(() => {
    const { type, assigned, createdBy } = filters;
    const isAllUsersSelected = !createdBy || createdBy === 'allUsers';
    const isCurrentUserSelected = createdBy === user?.uaa_id;

    const newState = {
      ...initialState,
      [PLAYLIST_TYPE.STANDARD]: type === PLAYLIST_TYPE.STANDARD,
      [PLAYLIST_TYPE.SONAR_RECOMMENDED]: type === PLAYLIST_TYPE.SONAR_RECOMMENDED,
      assigned: assigned === 'true',
      unassigned: assigned === 'false',
      allUsers: isAllUsersSelected,
      currentUser: isCurrentUserSelected,
    };
    setValue(newState);
  }, []);

  const filtersConfigurations = [];

  filtersConfigurations.push(
    {
      title: 'Created by:',
      type: 'radio',
      singleFilters: [
        {
          label: 'All users',
          id: 'allUsers',
        },
        {
          label: 'Me',
          id: 'currentUser',
        },
      ],
    },
    {
      title: 'Playlist state:',
      type: 'checkbox',
      singleFilters: [
        {
          label: 'Assigned',
          id: 'assigned',
          tooltip: 'Playlists that have been assigned',
        },
        {
          label: 'Unassigned',
          id: 'unassigned',
          tooltip: 'Playlists that haven’t been assigned',
        },
      ],
    }
  );

  if (isSonarEnabled) {
    filtersConfigurations.splice(1, 0, {
      title: 'Playlist type:',
      type: 'checkbox',
      singleFilters: [
        {
          label: 'Standard playlists',
          id: PLAYLIST_TYPE.STANDARD,
          tooltip: 'Created by you or other users on your account',
        },
        {
          label: 'Skill Sonar playlists',
          id: PLAYLIST_TYPE.SONAR_RECOMMENDED,
          tooltip: 'Auto-generated by Skill Sonar',
        },
      ],
    });
  }

  return (
    <Filters>
      {filtersConfigurations.map((filterConfig) => (
        <TypeFiltersContainer key={filterConfig.title}>
          <FilterTitle>{filterConfig.title}</FilterTitle>
          {filterConfig.type === 'radio'
            ? filterConfig.singleFilters && renderRadio(filterConfig.singleFilters)
            : filterConfig.singleFilters && renderCheckbox(filterConfig.singleFilters)}
        </TypeFiltersContainer>
      ))}
    </Filters>
  );
};

export default PlaylistFilters;
